import React, { useState } from "react";
import { Button, Alert, Badge } from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useAuthUser from "../../../components/Hooks/useAuthUser";
import {
  checkTenantHubspot,
  getHubspotOauthUrl,
  disconnectHubspot,
} from "../api/hubspotApi";
import { getUserRole } from "../../Users/api/usersApi";
import { translateApiError } from "../../../helpers/translateApiError";
import IntegrationCardLayout from "./IntegrationCardLayout";

const HubSpotIntegrationCard = () => {
  const { t } = useTranslation();
  const { tenantProfile } = useAuthUser();
  const [isLoading, setIsLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(getUserRole());

  // Fetch user role
  React.useEffect(() => {
    setCurrentUserRole(getUserRole());
  }, []);

  const isTenantUser = currentUserRole === "TENANT_USER";
  const canManageIntegrations = !isTenantUser;

  // Query HubSpot connection status
  const {
    data: hubspotStatus,
    isLoading: isCheckingConnection,
    error: queryError,
    refetch: refetchConnection,
  } = useQuery({
    queryKey: ["hubspot-status", tenantProfile?.handle],
    queryFn: () => checkTenantHubspot(tenantProfile.handle),
    enabled: !!tenantProfile?.handle,
    retry: 2,
    staleTime: 5000,
  });

  // Get connection status from API response
  const isConnected = hubspotStatus?.data?.connected || false;
  const hubspotHubId = hubspotStatus?.data?.hubspotHubId;
  const displayPortalName = hubspotStatus?.data?.portalDisplayName;
  const connectedBy =
    hubspotStatus?.data?.connectedBy || hubspotStatus?.data?.connectedByUser;

  // Handle connect to HubSpot
  const handleConnectToHubSpot = async () => {
    if (!tenantProfile.handle) {
      setConnectionError(t("integration.errors.tenantMissing"));
      return;
    }

    setIsLoading(true);
    setConnectionError(null);

    try {
      // Get OAuth URL from the authenticated API
      const response = await getHubspotOauthUrl(tenantProfile.handle);
      const oauthUrl = response.oauthUrl;

      // Open OAuth flow in a new tab
      const newTab = window.open(oauthUrl, "_blank", "noopener,noreferrer");

      // If popup opened successfully, listen for OAuth completion
      if (newTab) {
        const checkClosed = setInterval(() => {
          try {
            // Check if the tab is closed
            if (newTab.closed) {
              clearInterval(checkClosed);
              setIsLoading(false);
              // Refetch connection status after OAuth flow
              refetchConnection();
            }
          } catch (error) {
            // If we can't access newTab.closed, assume it's still open
            // This can happen due to CORS restrictions
          }
        }, 1000);
      } else {
        // Silently fail - user will need to manually retry if popup was blocked
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Failed to connect to HubSpot:", error);
      setConnectionError(t("integration.hubspot.connectFailed"));
      setIsLoading(false);
    }
  };

  // Handle disconnect from HubSpot
  const handleDisconnectFromHubSpot = async () => {
    if (!tenantProfile.handle) {
      setConnectionError(t("integration.errors.tenantMissing"));
      return;
    }

    setIsLoading(true);
    setConnectionError(null);

    try {
      await disconnectHubspot(tenantProfile.handle);
      // Refetch connection status after disconnect
      await refetchConnection();
    } catch (error) {
      console.error("Failed to disconnect from HubSpot:", error);
      setConnectionError(t("integration.hubspot.disconnectFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (isCheckingConnection) {
      return (
        <Badge className="integration-badge-disconnected">
          <span className="badge-dot"></span>
          {t("integration.status.checking")}
        </Badge>
      );
    }

    if (isConnected) {
      return (
        <Badge className="integration-badge-connected">
          <span className="badge-dot"></span>
          {t("integration.status.connected")}
        </Badge>
      );
    }

    return (
      <Badge className="integration-badge-disconnected">
        <span className="badge-dot"></span>
        {t("integration.status.notConnected")}
      </Badge>
    );
  };

  const fields = [
    [
      t("integration.details.hubspotPortalId"),
      isConnected && hubspotHubId
        ? displayPortalName
          ? `${displayPortalName} (${hubspotHubId})`
          : hubspotHubId
        : "-",
    ],
    [
      t("integration.details.connectedBy"),
      isConnected && connectedBy ? connectedBy : "-",
    ],
  ];

  const action = isConnected ? (
    <Button
      outline
      color="danger"
      className="btn-card-action"
      onClick={handleDisconnectFromHubSpot}
      disabled={isLoading || !canManageIntegrations}
      title={
        !canManageIntegrations ? t("integration.permissions.noDisconnect") : ""
      }
    >
      {isLoading
        ? t("integration.actions.disconnecting")
        : t("integration.actions.disconnect")}
    </Button>
  ) : (
    <Button
      color="primary"
      className="btn-card-action"
      onClick={handleConnectToHubSpot}
      disabled={isLoading || !tenantProfile?.handle || !canManageIntegrations}
      title={
        !canManageIntegrations ? t("integration.permissions.noConnect") : ""
      }
    >
      {isLoading
        ? t("integration.actions.connecting")
        : t("integration.actions.connect")}
    </Button>
  );

  return (
    <IntegrationCardLayout
      logo={require("../../../assets/images/hubspot-logo.png")}
      logoAlt={t("integration.hubspot.logoAlt")}
      badge={getStatusBadge()}
      action={action}
      description={t("integration.hubspot.description")}
      fields={fields}
      error={
        (connectionError || queryError) && (
          <Alert color="danger" className="mt-3 mb-0">
            {connectionError ||
              translateApiError(
                queryError,
                t("integration.errors.connectionError")
              )}
          </Alert>
        )
      }
    />
  );
};

export default HubSpotIntegrationCard;
